import {Tooltip, Box, Typography, Button} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  VerifiedUser as LicenseIcon,
} from '@mui/icons-material';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';
import {useNavigate} from 'react-router-dom';
import {ReactElement, cloneElement} from 'react';

interface LicenseWarningTooltipProps {
  children: ReactElement;
  action: 'add-unit' | 'batch-import';
  disabled?: boolean;
}

export default function LicenseWarningTooltip({
  children,
  action,
  disabled = false,
}: LicenseWarningTooltipProps) {
  const navigate = useNavigate();
  const {remainingUnits, isNearLimit, isAtLimit} = useLicenseQuota();

  const getTooltipContent = () => {
    if (isAtLimit) {
      return (
        <Box sx={{maxWidth: 280}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
            <ErrorIcon fontSize="small" sx={{color: '#DC2626'}} />
            <Typography variant="body2" sx={{fontWeight: 600, color: '#DC2626'}}>
              No Units Available
            </Typography>
          </Box>
          <Typography variant="body2" sx={{mb: 2}}>
            You've reached your license limit. Add more licenses to continue{' '}
            {action === 'add-unit' ? 'adding units' : 'importing units'}.
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate('/settings/license')}
            sx={{
              bgcolor: '#DC2626',
              '&:hover': {bgcolor: '#B91C1C'},
              fontSize: '0.75rem',
            }}
          >
            Add Licenses
          </Button>
        </Box>
      );
    }

    if (isNearLimit) {
      return (
        <Box sx={{maxWidth: 280}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
            <WarningIcon fontSize="small" sx={{color: '#D97706'}} />
            <Typography variant="body2" sx={{fontWeight: 600, color: '#D97706'}}>
              Low License Units
            </Typography>
          </Box>
          <Typography variant="body2" sx={{mb: 2}}>
            Only {remainingUnits} units remaining. Consider adding more licenses before{' '}
            {action === 'add-unit' ? 'adding units' : 'bulk importing'}.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/settings/license')}
            sx={{
              borderColor: '#D97706',
              color: '#D97706',
              '&:hover': {borderColor: '#B45309', bgcolor: '#FFFBEB'},
              fontSize: '0.75rem',
            }}
          >
            Manage Licenses
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{maxWidth: 250}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
          <LicenseIcon fontSize="small" sx={{color: '#059669'}} />
          <Typography variant="body2" sx={{fontWeight: 600}}>
            License Status
          </Typography>
        </Box>
        <Typography variant="body2">
          {remainingUnits} units available for{' '}
          {action === 'add-unit' ? 'adding new units' : 'bulk import'}.
        </Typography>
      </Box>
    );
  };

  // If at limit, disable the button and show error state
  if (isAtLimit && !disabled) {
    return (
      <Tooltip title={getTooltipContent()} arrow>
        {cloneElement(children, {
          disabled: true,
          sx: {
            ...children.props.sx,
            bgcolor: '#F3F4F6 !important',
            color: '#9CA3AF !important',
            '&:hover': {
              bgcolor: '#F3F4F6 !important',
            },
          },
        })}
      </Tooltip>
    );
  }

  // Show warning tooltip for near limit or info for normal state
  return (
    <Tooltip title={getTooltipContent()} arrow>
      {children}
    </Tooltip>
  );
}
